// @ts-nocheck
import * as React from "react";
import { Ic, PhoneInput, apiErrorMessage } from "../screens/common";
import { useEscapeKey } from "./useEscapeKey";
import { LocationPicker } from "./naver/LocationPicker";
import { api } from "../api";

// 어르신 명단 "신규 등록" 모달.
//
// 지금까지 Elderly 를 만드는 길은 회원가입(citizen 본인 / guardian 신규 어르신)
// 하나뿐이었다. 전화·방문 접수처럼 계정 없이 들어온 어르신을 운영자가 명부에
// 올릴 수 있게 하는 화면이며, 저장 후 발급되는 연결코드로 보호자가 나중에
// 가입해 스스로 연결한다("연결코드로 연결" 가입 경로).
//
// 좌표(LocationPicker)는 선택이지만 AI 자동매칭의 거리 점수에 직접 쓰이므로
// 지정 권장을 문구로 안내한다(미지정 시 서버가 중립 점수로 대체).
// RegionCreateModal 과 같은 골격(헤더/에러줄/2열 폼/푸터)을 따른다.

const HOUSEHOLDS = ["독거", "노인부부", "가족동거", "시설", "기타"];
const RELATIONS  = ["자녀", "배우자", "형제·자매", "친척", "기타"];
const RISKS = [
  { v: "stable",   l: "안정" },
  { v: "warning",  l: "주의" },
  { v: "critical", l: "위험" },
];

const EMPTY = {
  name: "", age: "", phone: "", address: "", sub_area: "",
  region_id: "", household_type: "", health_status: "", risk_level: "stable",
  latitude: null, longitude: null,
  guardian_name: "", guardian_phone: "", guardian_relation: "",
};

const coordOf = (f) =>
  f.latitude != null && f.longitude != null ? { lat: f.latitude, lng: f.longitude } : null;

export function ElderlyCreateModal({ user, onClose, onCreated }) {
  useEscapeKey(onClose);
  // 권역 관리자는 자기 권역 고정(서버도 강제) — 시스템 관리자만 선택 가능.
  const fixedRegionId = user?.region_id ?? null;
  const [form, setForm] = React.useState({ ...EMPTY, region_id: fixedRegionId ? String(fixedRegionId) : "" });
  const [regions, setRegions] = React.useState([]);
  const [submitting, setSubmitting] = React.useState(false);
  const [err, setErr] = React.useState(null);
  const [dupId, setDupId] = React.useState(null);   // 409 중복 시 기존 어르신 id
  const set = (k) => (v) => setForm(f => ({ ...f, [k]: v }));

  React.useEffect(() => {
    fetch("/api/v1/regions", { credentials: "same-origin" })
      .then(r => r.ok ? r.json() : [])
      .then(list => setRegions(Array.isArray(list) ? list : []))
      .catch(() => setRegions([]));
  }, []);

  // 선택한 권역의 중심을 지도 초기 위치로 — 매번 전국에서 찍어 들어가지 않게.
  const regionCenter = React.useMemo(() => {
    const r = regions.find(x => String(x.id) === String(form.region_id));
    return r && r.center_latitude != null && r.center_longitude != null
      ? { lat: r.center_latitude, lng: r.center_longitude } : null;
  }, [regions, form.region_id]);

  const submit = async (e) => {
    if (e) e.preventDefault();
    setErr(null); setDupId(null);
    if (!form.name.trim())  return setErr("어르신 성함을 입력해주세요.");
    if (!form.region_id)    return setErr("소속 권역을 선택해주세요.");
    setSubmitting(true);
    try {
      const created = await api.create("elderlies", {
        ...form,
        name: form.name.trim(),
        age: form.age === "" ? null : Number(form.age),
      });
      onCreated?.(created);
    } catch (e) {
      if (e?.status === 409) setDupId(e.body?.elderly_id ?? null);
      setErr(apiErrorMessage(e));
    } finally { setSubmitting(false); }
  };

  return (
    <div onClick={onClose}
      style={{position:'fixed', inset:0, background:'rgba(20,16,12,0.4)', display:'grid', placeItems:'center', zIndex:50, padding:20, overflow:'auto'}}>
      <form onSubmit={submit} onClick={e=>e.stopPropagation()} className="card"
        style={{width:'min(620px, 100%)', maxHeight:'90vh', overflow:'auto', padding:0}}>

        <div style={{padding:'16px 22px', borderBottom:'1px solid var(--border-soft)', display:'flex', alignItems:'center', justifyContent:'space-between', gap:12}}>
          <div>
            <div className="h3">어르신 신규 등록</div>
            <div className="muted" style={{fontSize:11, marginTop:2}}>전화·방문 접수 등 계정 없이 들어온 어르신을 명부에 올립니다</div>
          </div>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>{Ic.close}</button>
        </div>

        {err && (
          <div style={{margin:'14px 22px 0', padding:'10px 14px', background:'var(--rose-50)', color:'var(--rose-500)', borderRadius:10, fontSize:12.5}}>
            {err}
            {dupId && (
              <a href={`/elderly?id=${dupId}`} style={{marginLeft:8, color:'var(--orange-600)', fontWeight:700, textDecoration:'none'}}>
                기존 어르신 보기 →
              </a>
            )}
          </div>
        )}

        <div style={{padding:22, display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(220px, 1fr))', gap:14}}>
          <div>
            <div className="label">어르신 성함 *</div>
            <input className="input" value={form.name} onChange={e=>set('name')(e.target.value)} placeholder="예: 김순자" required autoFocus/>
          </div>
          <div>
            <div className="label">나이</div>
            <input className="input" type="number" min="0" max="130" value={form.age}
              onChange={e=>set('age')(e.target.value)} placeholder="세"/>
          </div>

          <div>
            <div className="label">연락처</div>
            <PhoneInput value={form.phone} onChange={e=>set('phone')(e.target.value)}/>
          </div>
          <div>
            <div className="label">가구형태</div>
            <select className="select" value={form.household_type} onChange={e=>set('household_type')(e.target.value)}>
              <option value="">— 선택 —</option>
              {HOUSEHOLDS.map(h => <option key={h}>{h}</option>)}
            </select>
          </div>

          <div style={{gridColumn:'1/-1'}}>
            <div className="label">주소</div>
            <input className="input" value={form.address} onChange={e=>set('address')(e.target.value)} placeholder="시/군/구 동 ..."/>
          </div>

          <div>
            <div className="label">소속 권역 *</div>
            {fixedRegionId ? (
              <input className="input" readOnly aria-readonly="true" tabIndex={-1}
                value={regions.find(r => String(r.id) === String(fixedRegionId))?.name || '내 권역'}
                title="권역 관리자는 담당 권역에만 등록할 수 있습니다."
                style={{background:'var(--bg-sub)', color:'var(--ink-3)', cursor:'not-allowed'}}/>
            ) : (
              <select className="select" value={form.region_id} onChange={e=>set('region_id')(e.target.value)} required>
                <option value="">— 선택 —</option>
                {regions.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            )}
          </div>
          <div>
            <div className="label">위험등급</div>
            <select className="select" value={form.risk_level} onChange={e=>set('risk_level')(e.target.value)}>
              {RISKS.map(r => <option key={r.v} value={r.v}>{r.l}</option>)}
            </select>
          </div>

          <div style={{gridColumn:'1/-1'}}>
            <div className="label">건강상태</div>
            <input className="input" value={form.health_status} onChange={e=>set('health_status')(e.target.value)}
              placeholder="예: 고혈압·당뇨, 거동 불편"/>
          </div>

          <div style={{gridColumn:'1/-1'}}>
            <div className="label">
              위치 <span className="muted" style={{fontWeight:400, fontSize:10.5, marginLeft:4}}>지도를 클릭해 자택 위치를 지정 — AI 매칭 거리 점수에 사용</span>
            </div>
            <LocationPicker
              value={coordOf(form)}
              onChange={(c)=>setForm(f => ({ ...f, latitude: c.lat, longitude: c.lng }))}
              center={regionCenter}
              height={200}/>
            {coordOf(form)
              ? <div style={{marginTop:6, fontSize:11.5, color:'var(--green-600, #15803d)'}}>
                  ✓ 위치 지정됨 <b className="mono">{form.latitude.toFixed(5)}, {form.longitude.toFixed(5)}</b>
                </div>
              : <div className="muted" style={{marginTop:6, fontSize:11}}>지정하지 않으면 매칭 거리 점수가 중립값으로 계산됩니다.</div>}
          </div>

          <div style={{gridColumn:'1/-1', borderTop:'1px solid var(--border-soft)', paddingTop:14, marginTop:2}}>
            <div className="label" style={{marginBottom:0}}>보호자 (선택)</div>
            <div className="muted" style={{fontSize:11, marginTop:4}}>
              보호자가 직접 가입할 예정이면 비워두세요. 저장 후 발급되는 <b>연결코드</b>로 본인이 연결합니다.
            </div>
          </div>
          <div>
            <div className="label">보호자 성함</div>
            <input className="input" value={form.guardian_name} onChange={e=>set('guardian_name')(e.target.value)} placeholder="비상시 연락할 보호자"/>
          </div>
          <div>
            <div className="label">보호자 연락처</div>
            <PhoneInput value={form.guardian_phone} onChange={e=>set('guardian_phone')(e.target.value)}/>
          </div>
          <div>
            <div className="label">어르신과의 관계</div>
            <select className="select" value={form.guardian_relation} onChange={e=>set('guardian_relation')(e.target.value)}>
              <option value="">— 선택 —</option>
              {RELATIONS.map(r => <option key={r}>{r}</option>)}
            </select>
          </div>
        </div>

        <div style={{padding:'14px 22px', borderTop:'1px solid var(--border-soft)', display:'flex', justifyContent:'flex-end', gap:8}}>
          <button type="button" className="btn btn-ghost btn-md" onClick={onClose}>취소</button>
          <button type="submit" className="btn btn-primary btn-md" disabled={submitting}>
            {submitting ? '등록 중…' : '등록'}
          </button>
        </div>
      </form>
    </div>
  );
}
